import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search, PawPrint, Plus, ArrowLeft, User } from "lucide-react";
import { NewClientDialog } from "@/pages/Clients";
import { toast } from "sonner";

// Search a client (owner) by phone/name → pick a pet (or add one / create client inline).
export function ClientPetPicker({ trigger, onPick, title = "Select client & pet" }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("search");
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [pets, setPets] = useState([]);
  const [addingPet, setAddingPet] = useState(false);
  const [petForm, setPetForm] = useState({ pet_name: "", species: "Dog", breed: "", dob: "" });

  const reset = () => { setStep("search"); setQ(""); setClients([]); setClient(null); setPets([]); setAddingPet(false); };

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try { const { data } = await api.get("/clients", { params: q ? { q } : {} }); setClients(data); }
    finally { setLoading(false); }
  };

  const openClient = async (c) => {
    const { data } = await api.get(`/clients/${c.id}`);
    setClient(data.client); setPets(data.pets); setStep("pets");
  };

  const addPet = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/clients/${client.id}/pets`, petForm);
      toast.success(`${data.pet_name} added`);
      setPets((p) => [data, ...p]); setAddingPet(false);
      setPetForm({ pet_name: "", species: "Dog", breed: "", dob: "" });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const pick = (pet) => { setOpen(false); reset(); onPick(pet); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { reset(); search(); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{step === "pets" ? client?.owner_name : title}</DialogTitle></DialogHeader>

        {step === "search" && (
          <>
            <form onSubmit={search} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="picker-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by phone or name…" className="pl-9" autoFocus />
              </div>
              <Button type="submit" variant="outline">Search</Button>
            </form>
            <div className="flex justify-end">
              <NewClientDialog trigger={<Button size="sm" variant="ghost" data-testid="picker-new-client"><Plus className="h-4 w-4 mr-1" />New Client</Button>} onCreated={(c) => openClient(c)} />
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-border">
              {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
              {!loading && clients.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground"><User className="h-8 w-8 mx-auto opacity-40 mb-2" />No clients found.</div>}
              {clients.map((c) => (
                <button key={c.id} type="button" data-testid={`picker-client-${c.id}`} onClick={() => openClient(c)}
                        className="w-full text-left px-3 py-3 hover:bg-muted/50 flex items-center justify-between">
                  <div><div className="font-medium text-sm">{c.owner_name}</div><div className="text-xs text-muted-foreground">{c.phone}</div></div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><PawPrint className="h-3 w-3" />{c.pets_count}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "pets" && (
          <>
            <button onClick={() => setStep("search")} className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-4 w-4" />All clients</button>
            <div className="text-xs text-muted-foreground">{client?.phone}</div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {pets.length === 0 && <div className="text-sm text-muted-foreground py-2">No pets yet — add one below.</div>}
              {pets.map((p) => (
                <button key={p.id} type="button" data-testid={`picker-pet-${p.id}`} onClick={() => pick(p)}
                        className="w-full text-left rounded-lg border border-border/60 px-3 py-2.5 hover:border-primary/50 hover:bg-primary/5 flex items-center gap-2">
                  <PawPrint className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">{p.pet_name}</span>
                  <span className="text-xs text-muted-foreground">{p.species}{p.breed ? ` · ${p.breed}` : ""} · {p.case_number}</span>
                </button>
              ))}
            </div>
            {addingPet ? (
              <form onSubmit={addPet} className="border-t border-border pt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Pet name *" value={petForm.pet_name} onChange={(e) => setPetForm({ ...petForm, pet_name: e.target.value })} required data-testid="picker-pet-name" />
                  <Input placeholder="Species" value={petForm.species} onChange={(e) => setPetForm({ ...petForm, species: e.target.value })} />
                  <Input placeholder="Breed" value={petForm.breed} onChange={(e) => setPetForm({ ...petForm, breed: e.target.value })} />
                  <div><Label className="text-xs">DOB</Label><Input type="date" value={petForm.dob} onChange={(e) => setPetForm({ ...petForm, dob: e.target.value })} /></div>
                </div>
                <div className="flex gap-2"><Button type="submit" size="sm" data-testid="picker-save-pet">Add pet</Button><Button type="button" size="sm" variant="ghost" onClick={() => setAddingPet(false)}>Cancel</Button></div>
              </form>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setAddingPet(true)} data-testid="picker-add-pet"><Plus className="h-4 w-4 mr-1" />Add another pet</Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
